import { useEffect, useRef } from "react";
import { usePrefersReducedMotion } from "../hooks/usePrefersReducedMotion";

// A static waveform shape, also the fallback when there is nothing to read.
const STATIC = [42, 68, 30, 88, 54, 78, 26, 64, 94, 46, 74, 34, 84, 50, 62, 28, 70, 40, 90, 56];

// Live bars for the playing preview, drawn as DOM bars rather than a canvas.
// Heights are written straight to the bars inside a rAF loop that runs only
// while this preview plays and motion is allowed.
export default function BeatBars({
  active,
  read,
}: {
  active: boolean;
  read: (buf: Uint8Array<ArrayBuffer>) => boolean;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const reduced = usePrefersReducedMotion();

  useEffect(() => {
    const root = ref.current;
    if (!active || reduced || !root) return;
    const bars = Array.from(root.children) as HTMLElement[];
    const buf = new Uint8Array(128);
    // The top of the spectrum is mostly empty for a beat; spread the bars
    // over the lower three quarters.
    const step = Math.max(1, Math.floor((buf.length * 0.75) / bars.length));
    let frame = 0;
    const tick = () => {
      if (read(buf)) {
        bars.forEach((bar, i) => {
          let sum = 0;
          for (let j = 0; j < step; j += 1) sum += buf[i * step + j] ?? 0;
          bar.style.height = `${Math.max(8, (sum / step / 255) * 100).toFixed(1)}%`;
        });
      }
      frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(frame);
      bars.forEach((bar, i) => (bar.style.height = `${STATIC[i]}%`));
    };
  }, [active, reduced, read]);

  return (
    <div ref={ref} aria-hidden className="flex h-7 min-w-0 flex-1 items-end gap-[2px]">
      {STATIC.map((h, i) => (
        <span
          key={i}
          className={`flex-1 rounded-sm ${active ? (i % 3 === 1 ? "bg-gold" : "bg-money") : "bg-chrome-dim/40"}`}
          style={{ height: `${h}%` }}
        />
      ))}
    </div>
  );
}
